import toast, { type Toast } from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { PhoneIncoming, X } from 'lucide-react';
import { cn } from '../ui/cn';
import {
  useIncomingCallPop,
  getExternalNumber,
  INCOMING_POP_DURATION_MS,
  type PhoneCallWithRelations,
} from '@/features/telephony';

/** Naam van de beller zoals het CRM hem kent, of null bij een onbekend nummer. */
function getCallerName(call: PhoneCallWithRelations): string | null {
  return (
    call.contacts?.name ?? call.leads?.contact_name ?? call.projects?.contact_person_name ?? null
  );
}

/** Contextregel onder de naam, bv. "Lead · Buitenschilderwerk" of "Project 2024-013 · Molenlei 11C". */
function getCallContext(call: PhoneCallWithRelations): string {
  if (call.lead_id) {
    return call.leads?.work_type ? `Lead · ${call.leads.work_type}` : 'Lead';
  }
  if (call.project_id) {
    if (!call.projects) return 'Project';
    const label = ['Project', call.projects.project_number].filter(Boolean).join(' ');
    const where = call.projects.address ?? call.projects.name;
    return where ? `${label} · ${where}` : label;
  }
  if (call.contact_id) return 'Contact';
  return 'Onbekend nummer';
}

/** Route van de kaart waar de oproep bij hoort. */
function getCallTarget(call: PhoneCallWithRelations): string {
  if (call.lead_id) return `/lead/${call.lead_id}`;
  if (call.project_id) return `/project/${call.project_id}`;
  if (call.contact_id) return '/dashboard?tab=contacts';
  return '/dashboard?tab=telefonie';
}

/** De screen-pop zelf: klik = naar de juiste kaart, kruisje = sluiten. */
function IncomingCallToast({ t, call }: { t: Toast; call: PhoneCallWithRelations }) {
  const navigate = useNavigate();
  const name = getCallerName(call);
  const number = getExternalNumber(call);
  const context = getCallContext(call);

  const openCard = () => {
    toast.dismiss(t.id);
    navigate(getCallTarget(call));
  };

  return (
    <div
      role="status"
      aria-live="assertive"
      className={cn(
        'pointer-events-auto w-full max-w-sm rounded-xl border border-theme-border bg-surface shadow-xl ring-1 ring-brand/30 transition-all duration-300',
        t.visible ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <div className="relative mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-success/10">
          <span
            className="absolute inline-flex h-full w-full animate-ping rounded-xl bg-success/30"
            aria-hidden="true"
          />
          <PhoneIncoming className="relative h-5 w-5 text-success" />
        </div>
        <button type="button" onClick={openCard} className="min-w-0 flex-1 text-left">
          <p className="text-xs font-semibold uppercase tracking-wide text-brand">
            Inkomende oproep
          </p>
          <p className="mt-0.5 truncate text-sm font-semibold text-foreground">{name ?? number}</p>
          <p className="mt-0.5 truncate text-xs text-foreground-secondary">
            {context}
            {name ? ` · ${number}` : ''}
          </p>
        </button>
        <button
          type="button"
          onClick={() => toast.dismiss(t.id)}
          className="shrink-0 rounded-lg p-1 text-foreground-tertiary transition-colors hover:bg-surface-secondary hover:text-foreground"
          aria-label="Sluit melding inkomende oproep"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/**
 * Globale screen-pop bij inkomende Voys-oproepen. Mount één keer in de
 * ingelogde Layout; luistert via realtime naar `phone_calls` en toont per
 * rinkelend gesprek één klikbare toast rechtsboven (auto-dismiss na 30 s).
 */
export default function IncomingCallPop() {
  useIncomingCallPop((call) => {
    toast.custom((t) => <IncomingCallToast t={t} call={call} />, {
      id: `incoming-call-${call.call_id}`,
      duration: INCOMING_POP_DURATION_MS,
      position: 'top-right',
    });
  });

  return null;
}
